import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  LinearProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material'
import {
  PlayArrow,
  Add,
  CheckCircle,
  Error,
  Refresh,
  Edit,
  Delete,
  ContentCopy,
} from '@mui/icons-material'
import { Button, Tag } from '@loadsmart/loadsmart-ui'
import { DevelopmentTranslationTestCase } from 'pages/TranslationMaps/types'
import TestCaseExecutionStatus from '../development/TestCase/TestCaseExecutionStatus'

export interface TestExecutionControlsProps {
  tests: DevelopmentTranslationTestCase[]
  onRunAllTests: () => void
  onRunTest: (testId: string) => void
  onCreateTest: () => void
  onEditTest?: (testId: string) => void
  onDeleteTest?: (testId: string) => void
  onCloneTest?: (testId: string) => void
  onShowExecutionResult?: (test: DevelopmentTranslationTestCase) => void
  isRunning: boolean
  isCreatingTest: boolean
}

export default function TestExecutionControls({
  tests,
  onRunAllTests,
  onRunTest,
  onCreateTest,
  onEditTest,
  onDeleteTest,
  onCloneTest,
  onShowExecutionResult,
  isRunning,
  isCreatingTest,
}: TestExecutionControlsProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [testToDelete, setTestToDelete] = useState<string | null>(null)
  const passedTests = tests.filter(test => test.execution?.success === true).length
  const failedTests = tests.filter(test => test.execution?.success === false).length
  const notExecutedTests = tests.filter(test => !test.execution).length
  const runningTests = tests.filter(test => test.isRunning).length

  const totalTests = tests.length
  const executedTests = passedTests + failedTests
  const successRate = executedTests > 0 ? (passedTests / executedTests) * 100 : 0

  const hasFailures = failedTests > 0
  const allPassed = executedTests > 0 && failedTests === 0 && runningTests === 0

  const handleEditTest = (testId: string) => {
    if (onEditTest) {
      onEditTest(testId)
    }
  }

  const handleDeleteClick = (testId: string) => {
    setTestToDelete(testId)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (testToDelete && onDeleteTest) {
      onDeleteTest(testToDelete)
    }
    setDeleteDialogOpen(false)
    setTestToDelete(null)
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setTestToDelete(null)
  }

  const handleCloneTest = (testId: string) => {
    if (onCloneTest) {
      onCloneTest(testId)
    }
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', py: 1.5, px: 2 }}
      >
        {/* Header */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
        >
          <Typography variant="h6">Test Execution</Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="secondary"
              onClick={onRunAllTests}
              disabled={isRunning || totalTests === 0}
              loading={isRunning}
            >
              <PlayArrow sx={{ mr: 1 }} />
              {isRunning ? 'Running Tests...' : 'Run All Tests'}
            </Button>
            <Button
              variant="primary"
              onClick={onCreateTest}
              disabled={isCreatingTest}
              loading={isCreatingTest}
            >
              <Add sx={{ mr: 1 }} />
              New Test Case
            </Button>
          </Stack>
        </Box>

        {/* Test Summary */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Tag variant="accent" size="small">
              <CheckCircle sx={{ mr: 0.5, fontSize: 16 }} />
              {`${passedTests} Passed`}
            </Tag>
            <Tag variant="danger" size="small">
              <Error sx={{ mr: 0.5, fontSize: 16 }} />
              {`${failedTests} Failed`}
            </Tag>
            <Tag variant="default" size="small">
              {`${notExecutedTests} Not Executed`}
            </Tag>
            {runningTests > 0 && (
              <Tag variant="accent" size="small">
                <Refresh sx={{ mr: 0.5, fontSize: 16 }} />
                {`${runningTests} Running`}
              </Tag>
            )}
          </Stack>

          {/* Progress Bar */}
          {totalTests > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption">Test Progress</Typography>
                <Typography variant="caption">
                  {executedTests}/{totalTests} ({Math.round(successRate)}% success)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(executedTests / totalTests) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: allPassed
                      ? 'accent.main'
                      : hasFailures
                      ? 'error.main'
                      : 'warning.main',
                  },
                }}
              />
            </Box>
          )}

          {/* Status Alert */}
          {hasFailures && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {failedTests} test(s) failed. Check the details below.
            </Alert>
          )}
        </Box>

        {/* Test List */}
        {totalTests > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Test Cases
              </Typography>
              <Tag variant="default" size="small">
                {`${totalTests} total`}
              </Tag>
              {executedTests > 0 && (
                <Tag
                  variant={allPassed ? 'success' : hasFailures ? 'danger' : 'warning'}
                  size="small"
                >
                  {`${executedTests} executed`}
                </Tag>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
              <Stack spacing={1.5}>
                {tests.map((test, index) => (
                  <Box
                    key={test.testCaseDetail?.id || index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'grey.50',
                        borderColor: 'primary.main',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                        {test.testCaseDetail?.description || `Test Case ${index + 1}`}
                      </Typography>
                      {test.testCaseDetail?.description && (
                        <Typography variant="caption" color="text.secondary">
                          Test #{index + 1}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                      <TestCaseExecutionStatus
                        test={test}
                        onClick={
                          test.execution && onShowExecutionResult
                            ? () => onShowExecutionResult(test)
                            : undefined
                        }
                      />
                      <Tooltip title="Run Test">
                        <IconButton
                          onClick={() =>
                            test.testCaseDetail?.id && onRunTest(test.testCaseDetail.id)
                          }
                          disabled={test.isRunning}
                          size="small"
                          color="primary"
                        >
                          <PlayArrow fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {onCloneTest && (
                        <Tooltip title="Clone Test">
                          <IconButton
                            onClick={() =>
                              test.testCaseDetail?.id && handleCloneTest(test.testCaseDetail.id)
                            }
                            size="small"
                            color="default"
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onEditTest && (
                        <Tooltip title="Edit Test">
                          <IconButton
                            onClick={() =>
                              test.testCaseDetail?.id && handleEditTest(test.testCaseDetail.id)
                            }
                            size="small"
                            color="default"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onDeleteTest && (
                        <Tooltip title="Delete Test">
                          <IconButton
                            onClick={() =>
                              test.testCaseDetail?.id && handleDeleteClick(test.testCaseDetail.id)
                            }
                            size="small"
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        )}

        {/* Empty State */}
        {totalTests === 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              py: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No test cases yet. Create your first test case to validate your mapping rules.
            </Typography>
            <Button variant="secondary" onClick={onCreateTest}>
              <Add sx={{ mr: 1 }} />
              Create Test Case
            </Button>
          </Box>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Test Case</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this test case? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="primary" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
